import { addDays, daysInMonth, diffDays, fmtDate, type IsoDate } from './dates';
import { fromCents, toCents } from './money';
import { saleItemCost } from './pos';
import type { ReportPeriod } from './reports';

/**
 * NEW — the financial statements (Raportet → Pasqyrat financiare): the statement of financial position, the
 * statement of comprehensive income and the statement of cash flows in the model of UA MF-Nr. 07/2019, Annex 1
 * (small and medium enterprises), built from the ERP's books and the director's finance register.
 *
 * The bases, printed with the statements as note 1:
 * - Revenue is what the till and the credit sales (debts) took in, less the VAT in it. The till keeps prices with
 *   VAT and no rate, so a line carries the rate of its product's latest purchase, 18 % when there is none.
 * - Cost of sales is what the goods sold cost: the buying price the till kept with each line, today's buying price
 *   for credit sales. The stock is valued at today's buying price. Goods registered without an invoice (the shop
 *   enters what it already had as it goes), counts and new buying prices change the stock without passing through
 *   the profit: they gather in Other reserves, shown on their own in note 3.
 * - Purchases count as paid on their date. What was owed to suppliers when the books started is an opening
 *   balance, paid down with `supplier_payment` entries.
 * - Opening balances are as at the end of their day; the statements count what happened after it.
 *
 * Amounts are added up in cents so the statement of financial position balances to the cent.
 */

// Check-constraint enum of finance_entries.kind (packages/db/supabase/migrations/20261023000100_finance_entries.sql)
export const FINANCE_OPENING_KINDS = [
  'opening_cash',
  'opening_equipment',
  'opening_payables',
  'opening_loans',
  'opening_capital',
] as const;
export const FINANCE_ENTRY_KINDS = [
  'rent',
  'wages',
  'utilities',
  'telecom',
  'services',
  'maintenance',
  'office',
  'transport',
  'marketing',
  'depreciation',
  'other_expense',
  'finance_cost',
  'profit_tax',
  'other_income',
  'vat_payment',
  'supplier_payment',
  'equipment',
  'loan_in',
  'loan_repayment',
  'capital_in',
  'dividend',
] as const;
export const FINANCE_KINDS = [...FINANCE_OPENING_KINDS, ...FINANCE_ENTRY_KINDS] as const;
export type FinanceOpeningKind = (typeof FINANCE_OPENING_KINDS)[number];
export type FinanceEntryKind = (typeof FINANCE_ENTRY_KINDS)[number];
export type FinanceKind = (typeof FINANCE_KINDS)[number];

export function isFinanceEntryKind(v: unknown): v is FinanceEntryKind {
  return (FINANCE_ENTRY_KINDS as readonly unknown[]).includes(v);
}

export interface FinanceKindInfo {
  label: string;
  /** What belongs here, under the field of the entry form. */
  hint: string;
}

export const FINANCE_KIND_INFO: Readonly<Record<FinanceEntryKind, FinanceKindInfo>> = {
  rent: { label: 'Qiraja', hint: 'Qiraja e lokalit, e depos ose e tokës.' },
  wages: { label: 'Pagat dhe kontributet', hint: 'Pagat, tatimi në paga dhe kontributet pensionale.' },
  utilities: { label: 'Rryma, uji dhe ngrohja', hint: 'Faturat e rrymës, ujit, ngrohjes dhe mbeturinave.' },
  telecom: { label: 'Telefoni dhe interneti', hint: 'Faturat e telefonit dhe të internetit.' },
  services: { label: 'Shërbimet', hint: 'Kontabilisti, avokati, programet, sigurimi dhe shërbime të tjera.' },
  maintenance: {
    label: 'Mirëmbajtja dhe riparimet',
    hint: 'Riparime të lokalit, të pajisjeve ose të automjeteve.',
  },
  office: { label: 'Materiali i zyrës dhe pastrimi', hint: 'Letra, materiali i zyrës dhe i pastrimit.' },
  transport: {
    label: 'Karburanti dhe transporti',
    hint: 'Nafta, benzina, transporti i mallit dhe dërgesat.',
  },
  marketing: { label: 'Reklamat', hint: 'Reklamat, fletëpalosjet dhe rrjetet sociale.' },
  depreciation: {
    label: 'Zhvlerësimi i pajisjeve',
    hint: 'Pjesa e vlerës së pajisjeve që konsumohet në vit (p.sh. 20 %). Nuk del nga arka.',
  },
  other_expense: {
    label: 'Shpenzime të tjera',
    hint: 'Gjoba, dhurata dhe shpenzime që nuk hyjnë diku tjetër.',
  },
  finance_cost: {
    label: 'Interesi dhe provizionet bankare',
    hint: 'Interesi i kredisë dhe provizionet e bankës.',
  },
  profit_tax: {
    label: 'Tatimi në fitim',
    hint: 'Tatimi në fitim ose tatimi tremujor në të ardhura i paguar në ATK.',
  },
  other_income: {
    label: 'Të hyra të tjera',
    hint: 'Të hyra jo nga shitja e mallit: qira e marrë, subvencione, kamata e bankës.',
  },
  vat_payment: { label: 'TVSH e paguar në ATK', hint: 'TVSH që i paguhet ATK-së me deklaratën mujore.' },
  supplier_payment: {
    label: 'Pagesë e borxhit të vjetër te furnitorët',
    hint: 'Vetëm për borxhet e gjendjes fillestare. Blerjet e regjistruara në ERP llogariten të paguara.',
  },
  equipment: {
    label: 'Blerje pajisjesh ose automjeti',
    hint: 'Gjëra që përdoren më shumë se një vit: rafte, kompjuterë, peshore, automjete.',
  },
  loan_in: { label: 'Kredi ose hua e marrë', hint: 'Paratë e kredisë ose të huasë që hynë në biznes.' },
  loan_repayment: {
    label: 'Kësti i kredisë',
    hint: 'Vetëm pjesa e kredisë; interesi shkon te “Interesi dhe provizionet bankare”.',
  },
  capital_in: { label: 'Kapital i futur nga pronari', hint: 'Para që pronari i fut në biznes.' },
  dividend: { label: 'Dividenda për pronarin', hint: 'Para që pronari i merr nga fitimi.' },
};

/** The entry form's choices, grouped the way the shop thinks of them. */
export const FINANCE_KIND_GROUPS: readonly { label: string; kinds: readonly FinanceEntryKind[] }[] = [
  {
    label: 'Shpenzimet e punës',
    kinds: [
      'rent',
      'wages',
      'utilities',
      'telecom',
      'services',
      'maintenance',
      'office',
      'transport',
      'marketing',
      'other_expense',
    ],
  },
  { label: 'Tatimet dhe banka', kinds: ['vat_payment', 'profit_tax', 'finance_cost'] },
  { label: 'Pajisjet', kinds: ['equipment', 'depreciation'] },
  { label: 'Kreditë dhe pronari', kinds: ['loan_in', 'loan_repayment', 'capital_in', 'dividend'] },
  { label: 'Të tjera', kinds: ['other_income', 'supplier_payment'] },
];

/** Where an entry lands in the statements, shown under the kind in the entry form. */
export const FINANCE_KIND_LINE: Readonly<Record<FinanceEntryKind, string>> = {
  rent: 'Shpenzimet administrative',
  wages: 'Shpenzimet administrative',
  utilities: 'Shpenzimet administrative',
  telecom: 'Shpenzimet administrative',
  services: 'Shpenzimet administrative',
  maintenance: 'Shpenzimet administrative',
  office: 'Shpenzimet administrative',
  transport: 'Shpenzimet e shpërndarjes',
  marketing: 'Shpenzimet e shpërndarjes',
  depreciation: 'Shpenzimet administrative; ul vlerën e pajisjeve',
  other_expense: 'Shpenzimet e tjera',
  finance_cost: 'Shpenzimet financiare',
  profit_tax: 'Shpenzimet e tatimit në fitim',
  other_income: 'Të ardhurat tjera',
  vat_payment: 'Ul TVSH-në për t’u paguar',
  supplier_payment: 'Ul llogaritë e pagueshme',
  equipment: 'Prona, pajisjet dhe impiantet',
  loan_in: 'Kreditë dhe huatë',
  loan_repayment: 'Ul kreditë dhe huatë',
  capital_in: 'Kapitali aksionar',
  dividend: 'Ul fitimet e mbajtura',
};

export const FINANCE_OPENING_FIELDS = ['cash', 'equipment', 'payables', 'loans', 'capital'] as const;
export type FinanceOpeningField = (typeof FINANCE_OPENING_FIELDS)[number];

export const FINANCE_OPENING_INFO: Readonly<Record<FinanceOpeningField, FinanceKindInfo>> = {
  cash: {
    label: 'Paraja në arkë dhe në bankë',
    hint: 'Sa para kishte biznesi në arkë dhe në llogaritë bankare.',
  },
  equipment: {
    label: 'Pajisjet dhe automjetet',
    hint: 'Vlera e mbetur e pajisjeve, inventarit dhe automjeteve të biznesit.',
  },
  payables: { label: 'Borxhet ndaj furnitorëve', hint: 'Faturat e furnitorëve që ende nuk ishin paguar.' },
  loans: { label: 'Kreditë dhe huatë', hint: 'Sa kishte mbetur për t’u paguar nga kreditë dhe huatë.' },
  capital: { label: 'Kapitali i pronarit', hint: 'Kapitali i regjistruar në ARBK.' },
};

export interface FinanceOpening {
  /** The balances are as at the end of this day. */
  date: IsoDate;
  cash: number;
  equipment: number;
  payables: number;
  loans: number;
  capital: number;
}

export interface FinanceEntryLike {
  id: string;
  date: IsoDate;
  kind: FinanceKind;
  amount: number;
  note: string;
}

export interface FinanceLineLike {
  product_id: string | null;
  qty: number;
  price: number;
  /** The buying price the till kept with the line; a credit sale's line has none. */
  cost?: number | null;
}

/** A till sale or a credit sale, with VAT. */
export interface FinanceSaleLike {
  date: IsoDate;
  total: number;
  items: readonly FinanceLineLike[];
}

export interface FinanceDebtLike extends FinanceSaleLike {
  customer_id: string;
}

export interface FinancePaymentLike {
  customer_id: string;
  date: IsoDate;
  amount: number;
}

export interface FinancePurchaseLike {
  date: IsoDate;
  total: number;
  vat_total: number;
}

export interface FinanceBooks {
  sales: readonly FinanceSaleLike[];
  debts: readonly FinanceDebtLike[];
  payments: readonly FinancePaymentLike[];
  purchases: readonly FinancePurchaseLike[];
  entries: readonly FinanceEntryLike[];
  /** The VAT % of each product's latest purchase (`product_vat_rates()`). */
  vatRates: ReadonlyMap<string, number>;
  /** Each product's buying price today. */
  productBuy: ReadonlyMap<string, number>;
  /** `stock_value_at()` of every day `financeStockDays()` names. */
  stock: ReadonlyMap<IsoDate, number>;
}

/** The opening balances saved with `save_finance_opening`, or null before the director has saved any. */
export function financeOpening(entries: readonly FinanceEntryLike[]): FinanceOpening | null {
  const rows = entries.filter((e) => e.kind.startsWith('opening_'));
  const first = rows[0];
  if (!first) return null;
  const amount = (field: FinanceOpeningField) => rows.find((e) => e.kind === `opening_${field}`)?.amount ?? 0;
  return {
    date: first.date,
    cash: amount('cash'),
    equipment: amount('equipment'),
    payables: amount('payables'),
    loans: amount('loans'),
    capital: amount('capital'),
  };
}

/** Without opening balances the books start at zero, the day before the first record. */
export function assumedFinanceOpening(
  books: Pick<FinanceBooks, 'sales' | 'debts' | 'payments' | 'purchases' | 'entries'>,
  today: IsoDate,
): FinanceOpening {
  let first = today;
  for (const list of [books.sales, books.debts, books.payments, books.purchases, books.entries]) {
    for (const x of list) if (x.date < first) first = x.date;
  }
  return { date: addDays(first, -1), cash: 0, equipment: 0, payables: 0, loans: 0, capital: 0 };
}

// ---------------------------------------------------------------------------------------------- periods

type Pair<T> = readonly [T, T];

export interface FinancePeriod {
  from: IsoDate;
  to: IsoDate;
}

export interface FinancePeriods {
  /** What the income and cash flow statements cover; null when it lies wholly outside the books. */
  current: FinancePeriod | null;
  /** The comparable period before it, as far as the books reach. */
  previous: FinancePeriod | null;
  /** The days of the statement of financial position: the end of the period and the day before it began. */
  days: Pair<IsoDate | null>;
}

function monthEnd(month: string): IsoDate {
  return `${month}-${String(daysInMonth(`${month}-01`)).padStart(2, '0')}`;
}

/**
 * The Reports controls as statement periods: a month, a year or a custom range, never before the day after the
 * opening balances and never past today. The comparison is the period just before: the previous day, month or
 * year, or a custom range of the same length.
 */
export function financePeriods(
  controls: { period: ReportPeriod; from: string; to: string },
  openingDay: IsoDate,
  today: IsoDate,
): FinancePeriods {
  const start = addDays(openingDay, 1);
  let nominal: FinancePeriod;
  let before: FinancePeriod | null = null;
  if (controls.from || controls.to) {
    nominal = { from: controls.from || start, to: controls.to || today };
    if (controls.from && controls.to && controls.from <= controls.to) {
      const length = diffDays(controls.from, controls.to) + 1;
      before = { from: addDays(controls.from, -length), to: addDays(controls.from, -1) };
    }
  } else if (controls.period === 'day') {
    nominal = { from: today, to: today };
    before = { from: addDays(today, -1), to: addDays(today, -1) };
  } else if (controls.period === 'month') {
    const month = today.slice(0, 7);
    const lastMonthEnd = addDays(`${month}-01`, -1);
    nominal = { from: `${month}-01`, to: monthEnd(month) };
    before = { from: `${lastMonthEnd.slice(0, 7)}-01`, to: lastMonthEnd };
  } else if (controls.period === 'year') {
    const year = Number(today.slice(0, 4));
    nominal = { from: `${year}-01-01`, to: `${year}-12-31` };
    before = { from: `${year - 1}-01-01`, to: `${year - 1}-12-31` };
  } else {
    nominal = { from: start, to: today };
  }
  const clamp = (p: FinancePeriod): FinancePeriod | null => {
    const from = p.from < start ? start : p.from;
    const to = p.to > today ? today : p.to;
    return from <= to ? { from, to } : null;
  };
  const current = clamp(nominal);
  return {
    current,
    previous: before && clamp(before),
    days: current ? [current.to, addDays(current.from, -1)] : [null, null],
  };
}

/** The days whose stock value the statements need: the opening day and both ends of each period. */
export function financeStockDays(periods: FinancePeriods, openingDay: IsoDate): IsoDate[] {
  const days = new Set<IsoDate>([openingDay]);
  for (const p of [periods.current, periods.previous]) {
    if (!p) continue;
    days.add(addDays(p.from, -1));
    days.add(p.to);
  }
  return [...days].sort();
}

/** `Viti 2026` for a whole calendar year, one day as its date, else `01/09/2026 – 30/09/2026`; `—` for none. */
export function financePeriodLabel(p: FinancePeriod | null): string {
  if (!p) return '—';
  if (p.from === p.to) return fmtDate(p.from);
  if (p.from.endsWith('-01-01') && p.to.endsWith('-12-31') && p.from.slice(0, 4) === p.to.slice(0, 4)) {
    return `Viti ${p.from.slice(0, 4)}`;
  }
  return `${fmtDate(p.from)} – ${fmtDate(p.to)}`;
}

// ---------------------------------------------------------------------------------------------- figures

/** Sales lines without a VAT rate of their own are taken to carry the standard rate. */
export const DEFAULT_SALES_VAT = 18;

/** VAT on the shop's own expenses and equipment is not tracked: the register holds what was paid. */
const DISTRIBUTION: readonly FinanceEntryKind[] = ['transport', 'marketing'];
const ADMINISTRATIVE: readonly FinanceEntryKind[] = [
  'rent',
  'wages',
  'utilities',
  'telecom',
  'services',
  'maintenance',
  'office',
  'depreciation',
];
/** Money that left the till or the bank (depreciation never does). */
const CASH_OUT: readonly FinanceEntryKind[] = [
  'rent',
  'wages',
  'utilities',
  'telecom',
  'services',
  'maintenance',
  'office',
  'transport',
  'marketing',
  'other_expense',
  'finance_cost',
  'profit_tax',
  'vat_payment',
  'supplier_payment',
  'equipment',
  'loan_repayment',
  'dividend',
];
const CASH_IN: readonly FinanceEntryKind[] = ['other_income', 'loan_in', 'capital_in'];

/** The VAT inside a sale's total, in cents: each line at its product's rate, the total split as the lines are. */
export function saleVatCents(sale: FinanceSaleLike, vatRates: ReadonlyMap<string, number>): number {
  let gross = 0;
  let vat = 0;
  for (const line of sale.items) {
    const value = line.qty * line.price;
    const rate = (line.product_id === null ? undefined : vatRates.get(line.product_id)) ?? DEFAULT_SALES_VAT;
    gross += value;
    vat += (value * rate) / (100 + rate);
  }
  const share = gross > 0 ? vat / gross : DEFAULT_SALES_VAT / (100 + DEFAULT_SALES_VAT);
  return Math.round(toCents(sale.total) * share);
}

type KindCents = Record<FinanceEntryKind, number>;

/** What the goods of a sale cost, in cents. */
function saleCostCents(sale: FinanceSaleLike, productBuy: ReadonlyMap<string, number>): number {
  const cost = sale.items.reduce(
    (sum, line) =>
      sum +
      line.qty * saleItemCost({ cost: line.cost, product_id: line.product_id }, (id) => productBuy.get(id)),
    0,
  );
  return toCents(cost);
}

/** What happened in a period, in cents. */
interface Flows {
  tillSales: number;
  creditSales: number;
  salesVat: number;
  tillCost: number;
  creditCost: number;
  received: number;
  purchases: number;
  purchasesVat: number;
  kinds: KindCents;
}

function flows(books: FinanceBooks, p: FinancePeriod): Flows {
  const inside = (date: IsoDate) => date >= p.from && date <= p.to;
  const kinds = Object.fromEntries(FINANCE_ENTRY_KINDS.map((k) => [k, 0])) as KindCents;
  const f: Flows = {
    tillSales: 0,
    creditSales: 0,
    salesVat: 0,
    tillCost: 0,
    creditCost: 0,
    received: 0,
    purchases: 0,
    purchasesVat: 0,
    kinds,
  };
  for (const s of books.sales) {
    if (!inside(s.date)) continue;
    f.tillSales += toCents(s.total);
    f.salesVat += saleVatCents(s, books.vatRates);
    f.tillCost += saleCostCents(s, books.productBuy);
  }
  for (const d of books.debts) {
    if (!inside(d.date)) continue;
    f.creditSales += toCents(d.total);
    f.salesVat += saleVatCents(d, books.vatRates);
    f.creditCost += saleCostCents(d, books.productBuy);
  }
  for (const x of books.payments) if (inside(x.date)) f.received += toCents(x.amount);
  for (const x of books.purchases) {
    if (!inside(x.date)) continue;
    f.purchases += toCents(x.total);
    f.purchasesVat += toCents(x.vat_total);
  }
  for (const e of books.entries) {
    if (isFinanceEntryKind(e.kind) && inside(e.date)) kinds[e.kind] += toCents(e.amount);
  }
  return f;
}

const sumKinds = (kinds: KindCents, list: readonly FinanceEntryKind[]) =>
  list.reduce((acc, k) => acc + kinds[k], 0);

/** The income statement of a period, in cents. */
interface Income {
  tillSales: number;
  creditSales: number;
  salesVat: number;
  revenue: number;
  purchasesNet: number;
  costOfSales: number;
  grossProfit: number;
  otherIncome: number;
  distribution: number;
  administrative: number;
  otherExpenses: number;
  operatingProfit: number;
  financeCosts: number;
  profitBeforeTax: number;
  tax: number;
  profit: number;
}

function income(f: Flows): Income {
  const k = f.kinds;
  const revenue = f.tillSales + f.creditSales - f.salesVat;
  const purchasesNet = f.purchases - f.purchasesVat;
  const costOfSales = f.tillCost + f.creditCost;
  const grossProfit = revenue - costOfSales;
  const distribution = sumKinds(k, DISTRIBUTION);
  const administrative = sumKinds(k, ADMINISTRATIVE);
  const operatingProfit = grossProfit + k.other_income - distribution - administrative - k.other_expense;
  const profitBeforeTax = operatingProfit - k.finance_cost;
  return {
    tillSales: f.tillSales,
    creditSales: f.creditSales,
    salesVat: f.salesVat,
    revenue,
    purchasesNet,
    costOfSales,
    grossProfit,
    otherIncome: k.other_income,
    distribution,
    administrative,
    otherExpenses: k.other_expense,
    operatingProfit,
    financeCosts: k.finance_cost,
    profitBeforeTax,
    tax: k.profit_tax,
    profit: profitBeforeTax - k.profit_tax,
  };
}

/** The balances at the end of a day, in cents. */
interface Position {
  cash: number;
  receivables: number;
  stock: number;
  equipment: number;
  /** VAT owed to ATK; below zero it is a VAT credit. */
  vat: number;
  /** Owed to suppliers, with what customers paid in advance. */
  payables: number;
  loans: number;
  capital: number;
  /** Stock registered without an invoice, counted or repriced since the books started. */
  reserves: number;
  retained: number;
}

/** What every customer owes at the end of a day; an overpayment is owed back to the customer. */
function customerBalances(books: FinanceBooks, day: IsoDate) {
  const balance = new Map<string, number>();
  const add = (customer: string, cents: number) =>
    balance.set(customer, (balance.get(customer) ?? 0) + cents);
  for (const d of books.debts) if (d.date <= day) add(d.customer_id, toCents(d.total));
  for (const p of books.payments) if (p.date <= day) add(p.customer_id, -toCents(p.amount));
  let receivables = 0;
  let credit = 0;
  for (const b of balance.values()) {
    if (b > 0) receivables += b;
    else credit -= b;
  }
  return { receivables, credit };
}

const stockOn = (books: FinanceBooks, day: IsoDate) => toCents(books.stock.get(day) ?? 0);

/** How much more the stock changed than purchases less cost of sales explain. */
const stockChangeOffBooks = (f: Flows, stockStart: number, stockEnd: number) =>
  stockEnd - stockStart - (f.purchases - f.purchasesVat) + f.tillCost + f.creditCost;

function position(books: FinanceBooks, opening: FinanceOpening, day: IsoDate): Position {
  const f = flows(books, { from: addDays(opening.date, 1), to: day });
  const k = f.kinds;
  const atOpening = customerBalances(books, opening.date);
  const atDay = customerBalances(books, day);
  const openingStock = stockOn(books, opening.date);
  const stock = stockOn(books, day);
  const o = {
    cash: toCents(opening.cash),
    equipment: toCents(opening.equipment),
    payables: toCents(opening.payables),
    loans: toCents(opening.loans),
    capital: toCents(opening.capital),
  };
  // whatever the opening balances leave over is the profit kept from before the books started
  const openingRetained =
    o.cash +
    o.equipment +
    atOpening.receivables -
    atOpening.credit +
    openingStock -
    o.payables -
    o.loans -
    o.capital;
  return {
    cash: o.cash + f.tillSales + f.received + sumKinds(k, CASH_IN) - f.purchases - sumKinds(k, CASH_OUT),
    receivables: atDay.receivables,
    stock,
    equipment: o.equipment + k.equipment - k.depreciation,
    vat: f.salesVat - f.purchasesVat - k.vat_payment,
    payables: o.payables - k.supplier_payment + atDay.credit,
    loans: o.loans + k.loan_in - k.loan_repayment,
    capital: o.capital + k.capital_in,
    reserves: stockChangeOffBooks(f, openingStock, stock),
    retained: openingRetained + income(f).profit - k.dividend,
  };
}

/** The lines of the statement of financial position, split where a balance changes side. */
function positionLines(p: Position) {
  const cash = Math.max(p.cash, 0);
  const otherCurrentAssets = Math.max(-p.vat, 0);
  const currentAssets = cash + p.receivables + p.stock + otherCurrentAssets;
  const nonCurrentAssets = p.equipment;
  const overdraft = Math.max(-p.cash, 0);
  const otherCurrentLiabilities = Math.max(p.vat, 0);
  const currentLiabilities = overdraft + p.payables + otherCurrentLiabilities;
  const equity = p.capital + p.reserves + p.retained;
  return {
    ...p,
    cash,
    otherCurrentAssets,
    currentAssets,
    nonCurrentAssets,
    assets: currentAssets + nonCurrentAssets,
    overdraft,
    otherCurrentLiabilities,
    currentLiabilities,
    liabilities: currentLiabilities + p.loans,
    equity,
    equityAndLiabilities: equity + currentLiabilities + p.loans,
  };
}
type PositionLines = ReturnType<typeof positionLines>;

/** One period: its income statement, its cash flows and the balances at both ends, in cents. */
function periodFigures(books: FinanceBooks, opening: FinanceOpening, p: FinancePeriod) {
  const dayBefore = addDays(p.from, -1);
  const f = flows(books, p);
  const k = f.kinds;
  const inc = income(f);
  const start = positionLines(position(books, opening, dayBefore));
  const end = positionLines(position(books, opening, p.to));
  // the stock bought and sold: what changed it without an invoice moved no money
  const stockBought = inc.purchasesNet - inc.costOfSales;
  const adjusted = inc.profit + k.depreciation + k.finance_cost + k.profit_tax;
  const fromOperations =
    adjusted -
    (end.receivables - start.receivables) -
    stockBought -
    (end.otherCurrentAssets - start.otherCurrentAssets) +
    (end.payables - start.payables) +
    (end.otherCurrentLiabilities - start.otherCurrentLiabilities);
  const operating = fromOperations - k.profit_tax - k.finance_cost;
  const investing = -k.equipment;
  const financing = k.capital_in - k.dividend + k.loan_in - k.loan_repayment;
  return {
    flows: f,
    income: inc,
    start,
    end,
    offBooks: stockChangeOffBooks(f, start.stock, end.stock),
    cashFlow: {
      stockBought,
      adjusted,
      fromOperations,
      operating,
      investing,
      financing,
      change: operating + investing + financing,
    },
  };
}
type PeriodFigures = ReturnType<typeof periodFigures>;

// ---------------------------------------------------------------------------------------------- statements

export type StatementRowKind = 'heading' | 'line' | 'subtotal' | 'total';

export interface StatementRow {
  kind: StatementRowKind;
  label: string;
  /** The note that explains the line. */
  note?: number;
  /** Euros for the current and the previous column; null where the books do not reach (shown as —). */
  values: Pair<number | null>;
  /** A line the short view leaves out while it is zero in both columns. */
  optional?: boolean;
  /** Rows of one section: its heading and subtotal leave the short view with its last line. */
  block?: string;
}

export interface Statement {
  title: string;
  /** `më 31/12/2026` or `për periudhën 01/01/2026 – 31/12/2026`. */
  subtitle: string;
  columns: Pair<string>;
  rows: readonly StatementRow[];
}

export interface FinanceNote {
  no: number;
  title: string;
  /** Sentences, for a note in words. */
  text?: readonly string[];
  rows?: readonly StatementRow[];
}

interface RowSpec<F> {
  kind: StatementRowKind;
  label: string;
  note?: number;
  value?: (f: F) => number;
  optional?: boolean;
  block?: string;
}

const pair = <A, B>(xs: Pair<A>, f: (x: A) => B): Pair<B> => [f(xs[0]), f(xs[1])];

function buildRows<F>(specs: readonly RowSpec<F>[], columns: Pair<F | null>): StatementRow[] {
  return specs.map(({ value, ...spec }) => ({
    ...spec,
    values: pair(columns, (f) => (f && value ? fromCents(value(f)) || 0 : null)),
  }));
}

const zero = () => 0;

const POSITION_ROWS: readonly RowSpec<PositionLines>[] = [
  { kind: 'heading', label: 'PASURITË' },
  { kind: 'heading', label: 'Pasuritë afatshkurtra', block: 'ca' },
  { kind: 'line', label: 'Paraja dhe ekuivalentët e parasë', note: 6, value: (p) => p.cash, block: 'ca' },
  { kind: 'line', label: 'Investimet financiare afatshkurtra', value: zero, optional: true, block: 'ca' },
  {
    kind: 'line',
    label: 'Llogaritë e arkëtueshme tregtare dhe të tjera',
    value: (p) => p.receivables,
    block: 'ca',
  },
  { kind: 'line', label: 'Stoqet', note: 3, value: (p) => p.stock, block: 'ca' },
  {
    kind: 'line',
    label: 'Pasuritë e tjera afatshkurtra',
    note: 7,
    value: (p) => p.otherCurrentAssets,
    optional: true,
    block: 'ca',
  },
  { kind: 'subtotal', label: 'Gjithsej pasuritë afatshkurtra', value: (p) => p.currentAssets, block: 'ca' },
  { kind: 'heading', label: 'Pasuritë afatgjata', block: 'nca' },
  { kind: 'line', label: 'Investimet në pjesëmarrje', value: zero, optional: true, block: 'nca' },
  { kind: 'line', label: 'Investimet në filiale', value: zero, optional: true, block: 'nca' },
  { kind: 'line', label: 'Investimet financiare afatgjata', value: zero, optional: true, block: 'nca' },
  { kind: 'line', label: 'Prona, pajisjet dhe impiantet', note: 5, value: (p) => p.equipment, block: 'nca' },
  { kind: 'line', label: 'Prona investuese', value: zero, optional: true, block: 'nca' },
  { kind: 'line', label: 'Investimet në vijim', value: zero, optional: true, block: 'nca' },
  { kind: 'line', label: 'Pasuritë e paprekshme', value: zero, optional: true, block: 'nca' },
  { kind: 'line', label: 'Pasuritë e shtyra tatimore', value: zero, optional: true, block: 'nca' },
  { kind: 'line', label: 'Pasuritë e tjera afatgjata', value: zero, optional: true, block: 'nca' },
  { kind: 'subtotal', label: 'Gjithsej pasuritë afatgjata', value: (p) => p.nonCurrentAssets, block: 'nca' },
  { kind: 'total', label: 'Gjithsej pasuritë', value: (p) => p.assets },
  { kind: 'heading', label: 'DETYRIMET DHE EKUITETI' },
  { kind: 'heading', label: 'Detyrimet afatshkurtra', block: 'cl' },
  { kind: 'line', label: 'Mbitërheqja bankare', value: (p) => p.overdraft, optional: true, block: 'cl' },
  {
    kind: 'line',
    label: 'Llogaritë e pagueshme tregtare dhe të tjera',
    value: (p) => p.payables,
    optional: true,
    block: 'cl',
  },
  { kind: 'line', label: 'Kreditë dhe huatë, pjesa afatshkurtër', value: zero, optional: true, block: 'cl' },
  { kind: 'line', label: 'Interesi i pagueshëm', value: zero, optional: true, block: 'cl' },
  { kind: 'line', label: 'Tatimi në fitim i pagueshëm', value: zero, optional: true, block: 'cl' },
  { kind: 'line', label: 'Provizionet afatshkurtra', value: zero, optional: true, block: 'cl' },
  {
    kind: 'line',
    label: 'Detyrimet ndaj lizingut, pjesa afatshkurtër',
    value: zero,
    optional: true,
    block: 'cl',
  },
  {
    kind: 'line',
    label: 'Detyrimet e tjera afatshkurtra',
    note: 7,
    value: (p) => p.otherCurrentLiabilities,
    optional: true,
    block: 'cl',
  },
  {
    kind: 'subtotal',
    label: 'Gjithsej detyrimet afatshkurtra',
    value: (p) => p.currentLiabilities,
    block: 'cl',
  },
  { kind: 'heading', label: 'Detyrimet afatgjata', block: 'ncl' },
  {
    kind: 'line',
    label: 'Kreditë dhe huatë, pjesa afatgjatë',
    value: (p) => p.loans,
    optional: true,
    block: 'ncl',
  },
  { kind: 'line', label: 'Provizionet afatgjata', value: zero, optional: true, block: 'ncl' },
  {
    kind: 'line',
    label: 'Detyrimet ndaj lizingut, pjesa afatgjatë',
    value: zero,
    optional: true,
    block: 'ncl',
  },
  { kind: 'line', label: 'Detyrimet e shtyra tatimore', value: zero, optional: true, block: 'ncl' },
  { kind: 'line', label: 'Detyrimet e tjera afatgjata', value: zero, optional: true, block: 'ncl' },
  { kind: 'subtotal', label: 'Gjithsej detyrimet afatgjata', value: (p) => p.loans, block: 'ncl' },
  { kind: 'subtotal', label: 'Gjithsej detyrimet', value: (p) => p.liabilities },
  { kind: 'heading', label: 'EKUITETI', block: 'eq' },
  { kind: 'line', label: 'Kapitali aksionar', value: (p) => p.capital, block: 'eq' },
  { kind: 'line', label: 'Fitimet e mbajtura', value: (p) => p.retained, block: 'eq' },
  { kind: 'line', label: 'Rezervat e tjera', note: 3, value: (p) => p.reserves, optional: true, block: 'eq' },
  { kind: 'subtotal', label: 'Gjithsej ekuiteti', value: (p) => p.equity, block: 'eq' },
  { kind: 'total', label: 'Gjithsej ekuiteti dhe detyrimet', value: (p) => p.equityAndLiabilities },
];

const INCOME_ROWS: readonly RowSpec<PeriodFigures>[] = [
  { kind: 'line', label: 'Të hyrat', note: 2, value: (f) => f.income.revenue },
  { kind: 'line', label: 'Kostoja e shitjes', note: 3, value: (f) => -f.income.costOfSales },
  { kind: 'subtotal', label: 'Fitimi / (humbja) bruto', value: (f) => f.income.grossProfit },
  { kind: 'line', label: 'Të ardhurat tjera', value: (f) => f.income.otherIncome, optional: true },
  {
    kind: 'line',
    label: 'Shpenzimet e shpërndarjes',
    note: 4,
    value: (f) => -f.income.distribution,
    optional: true,
  },
  { kind: 'line', label: 'Shpenzimet administrative', note: 4, value: (f) => -f.income.administrative },
  {
    kind: 'line',
    label: 'Shpenzimet e tjera',
    note: 4,
    value: (f) => -f.income.otherExpenses,
    optional: true,
  },
  { kind: 'subtotal', label: 'Fitimi / (humbja) operativ', value: (f) => f.income.operatingProfit },
  { kind: 'line', label: 'Shpenzimet financiare', value: (f) => -f.income.financeCosts, optional: true },
  { kind: 'line', label: 'Të ardhurat financiare', value: zero, optional: true },
  { kind: 'subtotal', label: 'Fitimi / (humbja) para tatimit', value: (f) => f.income.profitBeforeTax },
  { kind: 'line', label: 'Shpenzimet e tatimit në fitim', value: (f) => -f.income.tax, optional: true },
  { kind: 'total', label: 'Fitimi / (humbja) i/e vitit', value: (f) => f.income.profit },
  { kind: 'heading', label: 'Të ardhurat tjera gjithëpërfshirëse', block: 'oci' },
  {
    kind: 'line',
    label: 'Diferencat këmbimore nga përkthimi i operacioneve të huaja, neto nga tatimi',
    value: zero,
    optional: true,
    block: 'oci',
  },
  { kind: 'subtotal', label: 'Të ardhurat tjera gjithëpërfshirëse të vitit', value: zero, block: 'oci' },
  {
    kind: 'total',
    label: 'Gjithsej të ardhurat / (humbjet) gjithëpërfshirëse të vitit',
    value: (f) => f.income.profit,
  },
];

const CASH_FLOW_ROWS: readonly RowSpec<PeriodFigures>[] = [
  { kind: 'heading', label: 'Rrjedha e parasë nga aktivitetet operative' },
  { kind: 'line', label: 'Fitimi / (humbja) i / e vitit', value: (f) => f.income.profit },
  { kind: 'heading', label: 'Rregullimet për zërat jo në para të shpenzimeve dhe të hyrave', block: 'adj' },
  {
    kind: 'line',
    label: 'Zhvlerësimi i pronës, pajisjeve dhe impianteve',
    value: (f) => f.flows.kinds.depreciation,
    optional: true,
    block: 'adj',
  },
  {
    kind: 'line',
    label: 'Dëmtimi i pronës, pajisjeve dhe impianteve',
    value: zero,
    optional: true,
    block: 'adj',
  },
  { kind: 'line', label: 'Amortizimi i pasurive të paprekshme', value: zero, optional: true, block: 'adj' },
  { kind: 'line', label: 'Të ardhurat financiare', value: zero, optional: true, block: 'adj' },
  {
    kind: 'line',
    label: 'Shpenzimet financiare',
    value: (f) => f.flows.kinds.finance_cost,
    optional: true,
    block: 'adj',
  },
  {
    kind: 'line',
    label: '(Fitimi) / humbja nga shitja e pronës, pajisjeve dhe impianteve',
    value: zero,
    optional: true,
    block: 'adj',
  },
  {
    kind: 'line',
    label: 'Shpenzimet e tatimit në fitim',
    value: (f) => f.flows.kinds.profit_tax,
    optional: true,
    block: 'adj',
  },
  { kind: 'subtotal', label: 'Fitimi pas rregullimeve', value: (f) => f.cashFlow.adjusted, block: 'adj' },
  { kind: 'heading', label: 'Ndryshimet në pasuritë dhe detyrimet operative' },
  {
    kind: 'line',
    label: '(Rritja) / zvogëlimi në llogaritë e arkëtueshme tregtare dhe të tjera',
    value: (f) => f.start.receivables - f.end.receivables,
  },
  { kind: 'line', label: '(Rritja) / zvogëlimi në stoqe', value: (f) => -f.cashFlow.stockBought },
  {
    kind: 'line',
    label: '(Rritja) / zvogëlimi në pasuritë e tjera',
    value: (f) => f.start.otherCurrentAssets - f.end.otherCurrentAssets,
    optional: true,
  },
  {
    kind: 'line',
    label: 'Rritja / (zvogëlimi) në llogaritë e pagueshme tregtare dhe të tjera',
    value: (f) => f.end.payables - f.start.payables,
    optional: true,
  },
  {
    kind: 'line',
    label: 'Rritja / (zvogëlimi) në detyrimet e tjera',
    value: (f) => f.end.otherCurrentLiabilities - f.start.otherCurrentLiabilities,
    optional: true,
  },
  { kind: 'subtotal', label: 'Paraja e gjeneruar nga operacionet', value: (f) => f.cashFlow.fromOperations },
  {
    kind: 'line',
    label: 'Tatimi në fitim i paguar',
    value: (f) => -f.flows.kinds.profit_tax,
    optional: true,
  },
  { kind: 'line', label: 'Interesi i paguar', value: (f) => -f.flows.kinds.finance_cost, optional: true },
  {
    kind: 'subtotal',
    label: 'Rrjedha neto e parasë nga aktivitetet operative',
    value: (f) => f.cashFlow.operating,
  },
  { kind: 'heading', label: 'Rrjedha e parasë nga aktivitetet investuese', block: 'inv' },
  {
    kind: 'line',
    label: 'Pagesat për blerjet e pronës, pajisjeve dhe impianteve',
    value: (f) => -f.flows.kinds.equipment,
    optional: true,
    block: 'inv',
  },
  {
    kind: 'line',
    label: 'Arkëtimet për shitjet e pronës, pajisjeve dhe impianteve',
    value: zero,
    optional: true,
    block: 'inv',
  },
  {
    kind: 'line',
    label: 'Pagesat për blerjet e pasurive të paprekshme',
    value: zero,
    optional: true,
    block: 'inv',
  },
  { kind: 'line', label: 'Interesi i arkëtuar', value: zero, optional: true, block: 'inv' },
  { kind: 'line', label: 'Dividendat e arkëtuara', value: zero, optional: true, block: 'inv' },
  {
    kind: 'subtotal',
    label: 'Rrjedha neto e parasë nga aktivitetet investuese',
    value: (f) => f.cashFlow.investing,
    block: 'inv',
  },
  { kind: 'heading', label: 'Rrjedha e parasë nga aktivitetet financuese', block: 'fin' },
  {
    kind: 'line',
    label: 'Deponimet e kapitalit shtesë',
    value: (f) => f.flows.kinds.capital_in,
    optional: true,
    block: 'fin',
  },
  {
    kind: 'line',
    label: 'Dividendat e paguara',
    value: (f) => -f.flows.kinds.dividend,
    optional: true,
    block: 'fin',
  },
  {
    kind: 'line',
    label: 'Arkëtimet nga kreditë dhe huatë',
    value: (f) => f.flows.kinds.loan_in,
    optional: true,
    block: 'fin',
  },
  {
    kind: 'line',
    label: 'Ripagimi i kredive dhe huave',
    value: (f) => -f.flows.kinds.loan_repayment,
    optional: true,
    block: 'fin',
  },
  { kind: 'line', label: 'Pagesat e lizingut financiar', value: zero, optional: true, block: 'fin' },
  {
    kind: 'subtotal',
    label: 'Rrjedha neto e parasë nga aktivitetet financuese',
    value: (f) => f.cashFlow.financing,
    block: 'fin',
  },
  {
    kind: 'total',
    label: 'Rritja / (zvogëlimi) neto i parasë dhe ekuivalentëve të parasë',
    value: (f) => f.cashFlow.change,
  },
  {
    kind: 'line',
    label: 'Paraja dhe ekuivalentët e parasë në fillim',
    value: (f) => f.start.cash - f.start.overdraft,
  },
  {
    kind: 'total',
    label: 'Paraja dhe ekuivalentët e parasë në fund',
    note: 6,
    value: (f) => f.end.cash - f.end.overdraft,
  },
];

const EXPENSE_NOTE_KINDS: readonly FinanceEntryKind[] = [...DISTRIBUTION, ...ADMINISTRATIVE, 'other_expense'];

function noteRows(figures: Pair<PeriodFigures | null>) {
  const line = (
    label: string,
    value: (f: PeriodFigures) => number,
    optional = false,
  ): RowSpec<PeriodFigures> => ({
    kind: 'line',
    label,
    value,
    optional,
  });
  const total = (label: string, value: (f: PeriodFigures) => number): RowSpec<PeriodFigures> => ({
    kind: 'total',
    label,
    value,
  });
  const k = (kind: FinanceEntryKind) => (f: PeriodFigures) => f.flows.kinds[kind];
  return {
    revenue: buildRows(
      [
        line('Shitjet në arkë, me TVSH', (f) => f.income.tillSales),
        line('Shitjet me borxh, me TVSH', (f) => f.income.creditSales),
        line('TVSH e llogaritur në shitje', (f) => -f.income.salesVat),
        total('Të hyrat', (f) => f.income.revenue),
      ],
      figures,
    ),
    costOfSales: buildRows(
      [
        line('Kostoja e mallit të shitur në arkë', (f) => f.flows.tillCost),
        line('Kostoja e mallit të shitur me borxh', (f) => f.flows.creditCost),
        { kind: 'subtotal', label: 'Kostoja e shitjes', value: (f) => f.income.costOfSales },
        { kind: 'heading', label: 'Lëvizja e stokut' },
        line('Stoku në fillim të periudhës', (f) => f.start.stock),
        line('Blerjet e mallit, pa TVSH', (f) => f.income.purchasesNet),
        line('Kostoja e shitjes', (f) => -f.income.costOfSales),
        line(
          'Mall i regjistruar pa faturë, numërime dhe çmime të reja (te Rezervat e tjera)',
          (f) => f.offBooks,
        ),
        total('Stoku në fund të periudhës', (f) => f.end.stock),
      ],
      figures,
    ),
    expenses: buildRows(
      [
        ...EXPENSE_NOTE_KINDS.map((kind) => line(FINANCE_KIND_INFO[kind].label, k(kind), true)),
        total('Gjithsej shpenzimet', (f) => sumKinds(f.flows.kinds, EXPENSE_NOTE_KINDS)),
      ],
      figures,
    ),
    equipment: buildRows(
      [
        line('Vlera në fillim të periudhës', (f) => f.start.equipment),
        line('Blerjet e pajisjeve', k('equipment')),
        line('Zhvlerësimi', (f) => -f.flows.kinds.depreciation),
        total('Vlera në fund të periudhës', (f) => f.end.equipment),
      ],
      figures,
    ),
    cash: buildRows(
      [
        line('Paraja në fillim', (f) => f.start.cash - f.start.overdraft),
        line('Shitjet në arkë', (f) => f.flows.tillSales),
        line('Pagesat e klientëve për borxhet', (f) => f.flows.received),
        line('Të hyra të tjera, kredi dhe kapital', (f) => sumKinds(f.flows.kinds, CASH_IN), true),
        line('Blerjet e mallit, me TVSH', (f) => -f.flows.purchases),
        line(
          'Shpenzimet, tatimet, TVSH dhe interesi',
          (f) =>
            -sumKinds(f.flows.kinds, [
              ...EXPENSE_NOTE_KINDS.filter((x) => x !== 'depreciation'),
              'finance_cost',
              'profit_tax',
              'vat_payment',
            ]),
        ),
        line(
          'Pajisjet, këstet e kredisë, dividendat dhe borxhet e vjetra',
          (f) => -sumKinds(f.flows.kinds, ['equipment', 'loan_repayment', 'dividend', 'supplier_payment']),
          true,
        ),
        total('Paraja në fund', (f) => f.end.cash - f.end.overdraft),
      ],
      figures,
    ),
    vat: buildRows(
      [
        line('TVSH për t’u paguar në fillim', (f) => f.start.vat),
        line('TVSH e llogaritur në shitje', (f) => f.flows.salesVat),
        line('TVSH e zbritshme nga blerjet', (f) => -f.flows.purchasesVat),
        line('TVSH e paguar në ATK', (f) => -f.flows.kinds.vat_payment),
        total('TVSH për t’u paguar në fund (në minus: TVSH për t’u kthyer)', (f) => f.end.vat),
      ],
      figures,
    ),
  };
}

export interface FinanceStatements {
  opening: FinanceOpening;
  periods: FinancePeriods;
  position: Statement;
  income: Statement;
  cashFlow: Statement;
  notes: readonly FinanceNote[];
  /** The current column at a glance; null where the books do not reach. */
  kpis: { revenue: number | null; profit: number | null; cash: number | null; assets: number | null };
  /** Assets equal equity and liabilities in both columns. */
  balanced: boolean;
}

export function financeStatements(
  books: FinanceBooks,
  opening: FinanceOpening,
  periods: FinancePeriods,
): FinanceStatements {
  const figures = pair([periods.current, periods.previous], (p) =>
    p ? periodFigures(books, opening, p) : null,
  );
  const balances = pair(periods.days, (day) => (day ? positionLines(position(books, opening, day)) : null));
  const periodColumns = [financePeriodLabel(periods.current), financePeriodLabel(periods.previous)] as const;
  const periodSubtitle = periods.current
    ? `për periudhën ${fmtDate(periods.current.from)} – ${fmtDate(periods.current.to)}`
    : 'periudha është jashtë librave';
  const [current] = figures;
  const [atEnd] = balances;
  const notes = noteRows(figures);
  return {
    opening,
    periods,
    position: {
      title: 'Pasqyra e pozitës financiare',
      subtitle: periods.days[0] ? `më ${fmtDate(periods.days[0])}` : 'periudha është jashtë librave',
      columns: [fmtDate(periods.days[0]), fmtDate(periods.days[1])],
      rows: buildRows(POSITION_ROWS, balances),
    },
    income: {
      title: 'Pasqyra e të ardhurave gjithëpërfshirëse',
      subtitle: periodSubtitle,
      columns: periodColumns,
      rows: buildRows(INCOME_ROWS, figures),
    },
    cashFlow: {
      title: 'Pasqyra e rrjedhjes së parasë',
      subtitle: periodSubtitle,
      columns: periodColumns,
      rows: buildRows(CASH_FLOW_ROWS, figures),
    },
    notes: [
      {
        no: 1,
        title: 'Baza e përgatitjes',
        text: [
          'Pasqyrat janë përgatitur nga të dhënat e ERP-së, sipas modelit për ndërmarrjet e vogla dhe të mesme ' +
            'të UA MF-Nr. 07/2019, Shtojca Nr. 1. Shumat janë në euro.',
          `Gjendja fillestare është ajo e datës ${fmtDate(opening.date)}; pasqyrat llogarisin çka ndodhi pas saj.`,
          'Të hyrat janë shitjet në arkë dhe me borxh pa TVSH. TVSH llogaritet me normën e blerjes së fundit të ' +
            `produktit, ${DEFAULT_SALES_VAT} % kur produkti nuk është blerë kurrë.`,
          'Kostoja e shitjes është çmimi i blerjes i mallit të shitur, i ruajtur në arkë në çastin e shitjes; për ' +
            'shitjet me borxh, çmimi i fundit i blerjes. Stoku vlerësohet me çmimin e fundit të blerjes.',
          'Malli i regjistruar në ERP pa faturë blerjeje, numërimet e stokut dhe ndryshimet e çmimit të blerjes nuk ' +
            'hyjnë në fitim: paraqiten te Rezervat e tjera, të ndara në Shënimin 3, që kontabilisti t’i shqyrtojë.',
          'Blerjet e regjistruara në ERP llogariten të paguara në datën e faturës. Kreditë paraqiten të plota si ' +
            'afatgjata; pjesën që paguhet brenda 12 muajve e ndan kontabilisti.',
        ],
      },
      { no: 2, title: 'Të hyrat', rows: notes.revenue },
      { no: 3, title: 'Kostoja e shitjes dhe stoqet', rows: notes.costOfSales },
      { no: 4, title: 'Shpenzimet sipas llojit', rows: notes.expenses },
      { no: 5, title: 'Prona, pajisjet dhe impiantet', rows: notes.equipment },
      { no: 6, title: 'Paraja', rows: notes.cash },
      { no: 7, title: 'TVSH', rows: notes.vat },
    ],
    kpis: {
      revenue: current ? fromCents(current.income.revenue) : null,
      profit: current ? fromCents(current.income.profit) : null,
      cash: atEnd ? fromCents(atEnd.cash - atEnd.overdraft) : null,
      assets: atEnd ? fromCents(atEnd.assets) : null,
    },
    balanced: balances.every((b) => !b || b.assets === b.equityAndLiabilities),
  };
}

/** The rows to show: every ATK line, or (the short view) only the lines that hold an amount. */
export function statementRows(rows: readonly StatementRow[], everyLine: boolean): StatementRow[] {
  const holds = (r: StatementRow) => r.values.some((v) => v !== null && v !== 0);
  const shown = (r: StatementRow) => everyLine || !r.optional || holds(r);
  const liveBlocks = new Set(
    rows.filter((r) => r.kind === 'line' && r.block && shown(r)).map((r) => r.block),
  );
  return rows.filter((r) =>
    r.kind === 'line' ? shown(r) : !r.block || everyLine || liveBlocks.has(r.block),
  );
}

const amountFormat = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** `1.234,56`, a negative in brackets `(1.234,56)`, nothing as `-`, and `—` where the books do not reach. */
export function statementAmount(v: number | null): string {
  if (v === null) return '—';
  if (v === 0) return '-';
  return v < 0 ? `(${amountFormat.format(-v)})` : amountFormat.format(v);
}
